<template>
  <bulma-input ref="bulmaInput"
              :wrapper-class="computedWrapperClass"
              :icon="icon"
              :value="value"
              :placeholder="placeholder"
              :auto-complete="autoComplete"
              :type="revealed ? 'text' : 'password'"
              :disable-trim="true"
              :style-type="props.styleType"
              @input="emit('input', $event)"
              @enter="emit('enter')"
              @blur="emit('blur')">
    <template #icon-right>
      <button v-if="showToggle"
              class="button is-white is-small login-password-toggle-btn"
              type="button"
              aria-label="Toggle password visibility"
              @click.stop.prevent="toggleReveal">
        <span class="login-password-toggle-icon">
          <i :class="revealed ? 'fas fa-eye-slash' : 'fas fa-eye'" />
        </span>
      </button>
    </template>
  </bulma-input>
</template>

<script setup>
import { computed, onBeforeUnmount, ref } from 'vue'
import BulmaInput from './BulmaInput.vue'

const props = defineProps({
  value: {
    type: String,
    default: ''
  },
  styleType: {
    type: String,
    default: ''
  },
  placeholder: {
    type: String,
    default: ''
  },
  autoComplete: {
    type: String,
    default: 'current-password'
  },
  dataUnit: {
    type: String,
    default: ''
  },
  icon: {
    type: String,
    default: 'fas fa-key'
  },
  autofocus: {
    type: Boolean,
    default: false
  },
  remaskMs: {
    type: Number,
    default: 10000
  },
  showToggle: {
    type: Boolean,
    default: true
  }
})

const emit = defineEmits(['input', 'enter', 'blur'])

const revealed = ref(false)
const remaskTimeoutId = ref(null)
const bulmaInput = ref(null)

const hasStatusIconRight = computed(() => {
  return ['is-danger', 'is-success', 'is-info', 'is-warning'].includes(props.styleType)
})

const computedWrapperClass = computed(() => {
  return `login-password-control${hasStatusIconRight.value ? ' login-password-control--has-status-icon' : ''}`
})

function toggleReveal () {
  revealed.value = !revealed.value

  if (remaskTimeoutId.value) {
    clearTimeout(remaskTimeoutId.value)
    remaskTimeoutId.value = null
  }

  if (revealed.value) {
    remaskTimeoutId.value = setTimeout(() => {
      revealed.value = false
      remaskTimeoutId.value = null
    }, props.remaskMs)
  }
}

function focus () {
  bulmaInput.value?.focus?.()
}

onBeforeUnmount(() => {
  if (remaskTimeoutId.value) {
    clearTimeout(remaskTimeoutId.value)
    remaskTimeoutId.value = null
  }
})

defineExpose({ focus })
</script>

<style scoped>
.login-password-control {
  position: relative;
  display: block;
  height: auto;
  overflow: visible;
}

.login-password-control :deep(.input) {
  padding-right: 6.5rem;
}

.login-password-control .login-password-toggle-btn {
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
  width: 2.75rem;
  height: 2.75rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;
  box-shadow: none;
  cursor: pointer;
  opacity: 0.35;
  z-index: 10;
}

.login-password-control .login-password-toggle-btn:hover,
.login-password-control .login-password-toggle-btn:active,
.login-password-control .login-password-toggle-btn:focus {
  background: transparent;
  box-shadow: none;
}

.login-password-control--has-status-icon .login-password-toggle-btn {
  right: 1.4rem;
}

.login-password-control:focus-within .login-password-toggle-btn,
.login-password-control .login-password-toggle-btn:hover {
  opacity: 1;
}

.login-password-control .login-password-toggle-btn i {
  font-size: 1.25rem;
  pointer-events: none;
}

.login-password-control .login-password-toggle-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

:deep(input[type="password"]::-ms-reveal),
:deep(input[type="password"]::-ms-clear) {
  display: none;
}
</style>
